import React, { useState, useEffect } from "react";
import { X, Lock, UserPlus, KeyRound, Chrome, Mail } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const inputCls =
  "w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 text-sm text-[#374151] placeholder:text-[#9CA3AF] outline-none focus:ring-2 focus:ring-[#B3E0F2] transition-all";

export default function SignInModal({ isOpen, onClose, onRealLogin }) {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("parent");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "forgot") {
      setError("");
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/auth/forgot-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email }),
        });
        const body = await res.json();
        if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
        setForgotSent(true);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (mode === "signup" && password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const isSignup = mode === "signup";
      const res = await fetch(`${API}/api/accounts/${isSignup ? "signup" : "login"}`.replace("/accounts/login", "/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(isSignup ? { name, email, password, role } : { email, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
      onRealLogin(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in"
      data-testid="signin-modal"
      onClick={onClose}
    >
      <div
        className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-8 shadow-xl shadow-blue-900/5 animate-modal-in dark:bg-[#232D3D] dark:border-[#374457]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] dark:border-[#374457] pb-4">
          <h2 className="text-lg font-extrabold font-heading text-[#374151] dark:text-slate-100">
            {mode === "signin" ? "Sign in to NestTrack" : mode === "forgot" ? "Reset your password" : "Create your account"}
          </h2>
          <button
            onClick={onClose}
            data-testid="signin-close-btn"
            className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-full p-1 mb-4" data-testid="signin-mode-tabs">
          {[
            ["signin", "Sign In"],
            ["signup", "Create Account"],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setMode(key);
                setError("");
              }}
              data-testid={`signin-tab-${key}`}
              className={`flex-1 text-xs font-bold px-4 py-2 rounded-full transition-all ${mode === key ? "bg-[#F8C8DC] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"}`}
            >
              {label}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="master-login-form">
          {mode === "signup" && (
            <>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className={inputCls} data-testid="signup-name-input" />
              <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls} data-testid="signup-role-select">
                <option value="parent">Parent / Guardian</option>
                <option value="daycare_admin">Daycare Owner / Director</option>
                <option value="caregiver">Caregiver (Marketplace)</option>
                <option value="staff">Daycare Staff</option>
              </select>
            </>
          )}
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} data-testid="login-email-input" />
          {mode !== "forgot" && (
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Password (min 8 characters)" : "Password"}
              className={inputCls}
              data-testid="login-password-input"
            />
          )}
          {mode === "forgot" && forgotSent && (
            <p className="text-xs font-bold text-[#2F6031] bg-[#E2F4E1] border border-[#C4E5C2] rounded-xl px-3 py-2" data-testid="forgot-success">
              If an account exists for that email, a reset link is on its way — check your inbox (and spam).
            </p>
          )}
          {error && (
            <p className="text-xs font-bold text-rose-500 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full px-5 py-2.5 bg-[#374151] hover:bg-[#2b3441] text-white rounded-full text-sm font-bold transition-all duration-200 disabled:opacity-50"
            data-testid="login-submit-btn"
          >
            {loading ? "Please wait..." : mode === "signin" ? "Sign In" : mode === "forgot" ? "Send Reset Link" : "Create Account"}
          </button>
          <div className="flex items-center gap-3">
            <span className="flex-1 border-t border-[#FCE6E9] dark:border-[#374457]" />
            <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">or</span>
            <span className="flex-1 border-t border-[#FCE6E9] dark:border-[#374457]" />
          </div>
          <button
            type="button"
            onClick={loginWithGoogle}
            className="w-full px-5 py-2.5 bg-white hover:bg-[#E6F2FA] border border-[#B3E0F2] text-[#374151] rounded-full text-sm font-bold transition-all duration-200 flex items-center justify-center gap-2 dark:bg-[#273244] dark:text-slate-100"
            data-testid="google-signin-btn"
          >
            <Chrome className="w-4 h-4 text-[#5CB3DC]" /> Continue with Google
          </button>
          {mode === "signin" && (
            <button
              type="button"
              onClick={() => {
                setMode("forgot");
                setForgotSent(false);
                setError("");
              }}
              className="w-full text-center text-[11px] font-bold text-[#5CB3DC] hover:underline"
              data-testid="forgot-password-link"
            >
              <Mail className="w-3 h-3 inline mr-1 -mt-0.5" /> Forgot password? Email me a reset link
            </button>
          )}
          <p className="text-[10px] text-[#9CA3AF] text-center font-medium pt-1 flex items-center justify-center gap-1.5">
            {mode === "signin" ? (
              <>
                <Lock className="w-3 h-3" /> NestTrack staff sign in here · Invited? Use your claim link first
              </>
            ) : (
              <>
                <UserPlus className="w-3 h-3" /> Parents, daycare owners, caregivers & staff welcome · invited? Use your claim link
              </>
            )}
          </p>
        </form>
      </div>
    </div>
  );
}

// Single-use invite claim: user sets their own password from the emailed link
export const ResetModal = ({ token, onClose, onReset }) => {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
      onReset(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="reset-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-8 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-3">
          <h2 className="text-lg font-extrabold font-heading text-[#374151] flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#E5A0BC]" /> Choose a new password
          </h2>
          <button onClick={onClose} data-testid="reset-close-btn" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 characters)" className={inputCls} data-testid="reset-password-input" />
          <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password" className={inputCls} data-testid="reset-confirm-input" />
          {error && (
            <p className="text-xs font-bold text-rose-500 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="reset-error">
              {error}
            </p>
          )}
          <button type="submit" disabled={loading} className="w-full px-5 py-2.5 bg-[#374151] hover:bg-[#2b3441] text-white rounded-full text-sm font-bold transition-all disabled:opacity-50" data-testid="reset-submit-btn">
            {loading ? "Updating..." : "Update Password & Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
};

// Single-use invite claim: user sets their own password from the emailed link
export const ClaimModal = ({ token, onClose, onClaimed }) => {
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/accounts/claim/${token}`);
        const body = await res.json();
        if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
        setInfo(body);
      } catch (err) {
        setError(err.message);
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/accounts/claim`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(formatApiErrorDetail(body.detail));
      onClaimed(body);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#374151]/30 backdrop-blur-sm flex items-center justify-center p-4 animate-overlay-in" data-testid="claim-modal" onClick={onClose}>
      <div className="bg-white border border-[#FCE6E9] rounded-[2rem] max-w-md w-full p-8 shadow-xl animate-modal-in" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4 border-b border-[#FCE6E9] pb-4">
          <h2 className="text-lg font-extrabold font-heading text-[#374151] flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#E5A0BC]" /> Claim your account
          </h2>
          <button onClick={onClose} data-testid="claim-close-btn" className="w-8 h-8 rounded-full flex items-center justify-center text-[#9CA3AF] hover:bg-[#FCE6E9] hover:text-[#374151] transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        {info ? (
          <form onSubmit={submit} className="space-y-3" data-testid="claim-form">
            <p className="text-xs text-[#6B7280] font-medium">
              Welcome, <span className="font-extrabold text-[#374151]">{info.name}</span> ({info.email}) — choose a password to activate your {info.role === "daycare_admin" ? "daycare owner" : info.role} account.
            </p>
            {info.role === "caregiver" && (
              <p className="text-[11px] text-[#3D7E9E] font-bold bg-[#E6F2FA] border border-[#B3E0F2] rounded-xl px-3 py-2" data-testid="claim-role-hint">
                After activating, you'll publish your caregiver profile in Care Connections so families can find you.
              </p>
            )}
            {info.role === "daycare_admin" && (
              <p className="text-[11px] text-[#3D7E9E] font-bold bg-[#E6F2FA] border border-[#B3E0F2] rounded-xl px-3 py-2" data-testid="claim-role-hint">
                After activating, you'll set up your daycare facility so families can enroll.
              </p>
            )}
            <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 characters)" className={inputCls} data-testid="claim-password-input" />
            <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm password" className={inputCls} data-testid="claim-confirm-input" />
            {error && (
              <p className="text-xs font-bold text-rose-500 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2" data-testid="claim-error">
                {error}
              </p>
            )}
            <button type="submit" disabled={loading} className="w-full px-5 py-2.5 bg-[#374151] hover:bg-[#2b3441] text-white rounded-full text-sm font-bold transition-all disabled:opacity-50" data-testid="claim-submit-btn">
              {loading ? "Activating..." : "Activate Account"}
            </button>
          </form>
        ) : (
          <p className="text-xs text-[#9CA3AF] font-medium py-4 text-center" data-testid="claim-loading">
            {error || "Validating your invite link…"}
          </p>
        )}
      </div>
    </div>
  );
};
